import AppSetting from "../../config/AppSetting";
import UrlParameterEncoder from "../encoding/UrlParameterEncoder";
import ErrorStorage from "../store/ErrorStorage";
import EncryptUtils from "../encoding/EncryptUtils";
import { isFunction, isString, isUndefined } from "lodash-es";
import ListColumnBase from "app/commonComponents/listBase/listColumnBase";
import { globalPendingRequests } from "./utils";
import RequestOption from "./RequestOption";
const { generateGUID } = ListColumnBase();

const generateRequestKey = (url: string, body: any) => {
  if (isString(body) || isUndefined(body)) {
    return url + body + location.pathname;
  }
  return url + generateGUID();
};

export default class RestRequest {
  url: string;
  requestOption: RequestOption;
  /**
   * @param {string} url
   * @param {RequestOption} requestOption
   */
  constructor(url: string, requestOption: RequestOption) {
    this.url = url;
    this.requestOption = requestOption;
  }

  configAsyncRequestHeaders(isAuthenticated: boolean) {
    if (isAuthenticated) {
      // @ts-ignore
      this.requestOption.headers["request_page"] = window.location.href;
      // @ts-ignore
      this.requestOption.headers["request_secret"] = EncryptUtils.encryptMD5(
        window.location.href +
          AppSetting.localStorageValue.user.get().identityUserInfo.internalUserID
      );
    }

    // @ts-ignore
    if (this.requestOption.requireAuth) {
      // @ts-ignore
      this.requestOption.headers["Authorization"] =
        "Bearer " + AppSetting.localStorageValue.token.accessToken.get();
      // @ts-ignore
      this.requestOption.headers["RefreshToken"] =
        AppSetting.localStorageValue.token.refreshToken.get();
    }
  }

  configAsyncRequestBody() {
    // @ts-ignore
    const method = this.requestOption.method.toLowerCase() as keyof typeof this.nonRequestBodyVerbs;
    // @ts-ignore
    if (this.nonRequestBodyVerbs[method] && this.requestOption.body) {
      let queryObject;
      try {
        // @ts-ignore
        queryObject = JSON.parse(this.requestOption.body);
      } catch (error) {
        // @ts-ignore
        queryObject = this.requestOption.body;
      }
      // @ts-ignore
      if (this.requestOption.noEnCode) {
        // @ts-ignore
        this.url += "?" + new UrlParameterEncoder(queryObject).getNotEncodeQueryString();
      } else {
        // @ts-ignore
        this.url += "?" + new UrlParameterEncoder(queryObject).getQueryString();
      }
      // @ts-ignore
      delete this.requestOption.body;
    }
  }

  launchAsyncRequest() {
    const isAuthenticated = JSON.parse(localStorage.getItem("isAuthenticated") as string);
    const isLogin = this.url.toLowerCase().includes("login");
    if (!isAuthenticated && !isLogin) {
      window.location.reload();
      return;
    }

    this.configAsyncRequestHeaders(isAuthenticated);
    this.configAsyncRequestBody();

    const logoutButton = document.getElementById("logout-btn");

    // @ts-ignore
    const requestKey = generateRequestKey(this.url, this.requestOption.body);
    const existingRequest = globalPendingRequests.get(requestKey);
    if (existingRequest) {
      globalPendingRequests.set(requestKey, {
        requestPromise: existingRequest.requestPromise,
        callbacks: [...existingRequest.callbacks, this.requestOption],
      });
      return existingRequest.requestPromise;
    }

    // Create a new promise for this request
    // @ts-ignore
    const requestPromise = fetch(this.url, this.requestOption)
      .then((response) => {
        if (response.status === 401) {
          AppSetting.localStorageValue.setUnauthenticated();
          logoutButton?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
          throw new Error("UNAUTHORIZED");
        }
        this._checkStatus(response) && this._refreshAccessToken(response);
        if (response.headers.get("content-type")?.includes("application/json")) {
          return response.json();
        } else {
          return response;
        }
      })
      .then((result) => {
        const existingRequest = globalPendingRequests.get(requestKey);
        globalPendingRequests.delete(requestKey);

        if (existingRequest) {
          const callbacks = [...existingRequest.callbacks];
          callbacks.forEach((callback) => {
            // @ts-ignore
            isFunction(callback.onSuccess) && callback.onSuccess(result, callback.returnPara);
          });
        }
        return result;
      })
      .catch((error) => {
        const existingRequest = globalPendingRequests.get(requestKey);
        globalPendingRequests.delete(requestKey);
        if (error.message === "UNAUTHORIZED") {
          window.location.reload();
          return;
        }
        if (existingRequest) {
          const callbacks = [...existingRequest.callbacks];
          callbacks.forEach((callback) => {
            // @ts-ignore
            isFunction(callback.onError) && callback.onError(error, callback.returnPara);
          });
        }
        return Promise.reject(error);
      });

    globalPendingRequests.set(requestKey, {
      requestPromise,
      callbacks: [this.requestOption],
    });

    return requestPromise;
  }

  async launchSyncRequest() {
    const isAuthenticated = JSON.parse(localStorage.getItem("isAuthenticated") as string);
    const isLogin = this.url.toLowerCase().includes("login");
    if (!isAuthenticated && !isLogin) {
      window.location.reload();
      return;
    }

    this.configAsyncRequestHeaders(isAuthenticated);
    this.configAsyncRequestBody();

    // @ts-ignore
    const requestKey = generateRequestKey(this.url, this.requestOption.body);
    const existingRequest = globalPendingRequests.get(requestKey);
    if (existingRequest) {
      globalPendingRequests.set(requestKey, {
        requestPromise: existingRequest.requestPromise,
        callbacks: [...existingRequest.callbacks, this.requestOption],
      });
      return existingRequest.requestPromise;
    }

    // Create a new promise for this request
    const requestPromise = new Promise((resolve, reject) => {
      // @ts-ignore
      fetch(this.url, this.requestOption)
        .then(async (response) => {
          if (response.status === 401) {
            const logoutButton = document.getElementById("logout-btn");
            AppSetting.localStorageValue.setUnauthenticated();
            logoutButton?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
            throw new Error("UNAUTHORIZED");
          }
          if (!response.ok) {
            ErrorStorage.setError(response.status, response.statusText);
            const errorText = await response.text();
            throw new Error(errorText || response.statusText);
          }
          this._refreshAccessToken(response);
          // Always parse as JSON since sometime is Response object, this will fail into catch case
          const data = await response.json();
          return data;
        })
        .then(async (result) => {
          const existingRequest = globalPendingRequests.get(requestKey);
          globalPendingRequests.delete(requestKey);

          if (existingRequest) {
            const callbacks = [...existingRequest.callbacks];
            callbacks.forEach((callback) => {
              // @ts-ignore
              isFunction(callback.onSuccess) && callback.onSuccess(result, callback.returnPara);
            });
          }

          resolve(result);
        })
        .catch((error) => {
          const existingRequest = globalPendingRequests.get(requestKey);
          globalPendingRequests.delete(requestKey);
          if (error.message === "UNAUTHORIZED") {
            window.location.reload();
            return;
          }
          if (existingRequest) {
            const callbacks = [...existingRequest.callbacks];
            callbacks.forEach((callback) => {
              // @ts-ignore
              isFunction(callback.onError) && callback.onError(error, callback.returnPara);
            });
          }
          return reject(error);
        });
    });

    globalPendingRequests.set(requestKey, {
      requestPromise,
      callbacks: [this.requestOption],
    });

    return requestPromise;
  }

  _refreshAccessToken(response: Response) {
    // @ts-ignore
    if (!this.requestOption.requireAuth) {
      return;
    }

    const authorization = response.headers.get("Authorization");

    if (authorization) {
      let access = authorization.replace("Bearer ", "");
      AppSetting.localStorageValue.refreshAccessToken(access);
    }
  }

  _checkStatus(response: Response) {
    if (!response.ok) {
      ErrorStorage.setError(response.status, response.statusText);
      throw Error(String(response.status));
    }

    return true;
  }

  get nonRequestBodyVerbs() {
    return {
      post: false,
      head: true,
      get: true,
      put: false,
      patch: false,
      delete: false,
      options: true,
      connect: true,
      trace: true,
      Patch: false,
    };
  }
}
